import {useLazyQuery} from "@apollo/client";
import debounce from "lodash/debounce";
import {ForwardedRef, forwardRef, useCallback, useState} from "react";
import {FiSearch} from "react-icons/fi";
import {SelectInstance} from "react-select";

import {Select} from "@/components/select/Select.tsx";
import {SelectProps} from "@/components/select/Select.types.ts";
import {GQL_CREDENTIALS_QUERY} from "@/lib/queries/credentials.ts";
import {ResponseStatus} from "@/lib/types/common.ts";
import {CredentialListResponse, Credentials} from "@/lib/types/credentials.ts";

export type CredentialOptionType = {value: Credentials; label: string};

export const CredentialSelect = forwardRef(
  (props: SelectProps<CredentialOptionType>, ref: ForwardedRef<SelectInstance<CredentialOptionType>>) => {
    const [getCredentials] = useLazyQuery<CredentialListResponse>(GQL_CREDENTIALS_QUERY, {
      fetchPolicy: "no-cache",
    });
    const [reloadSelectIndicator, setReloadSelectIndicator] = useState<number>(1);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debounceAsyncGetCredentials = useCallback(
      debounce((inputValue, setOptions) => {
        getCredentials({
          variables: {
            search: inputValue ? inputValue : undefined,
            limit: 50,
          },
        }).then(({data}) => {
          if (data?.response?.status === ResponseStatus.SUCCESS) {
            const credentials = data?.response.data.map((credential) => ({
              value: credential,
              label: credential.display_name,
            }));
            setOptions(credentials);
            return;
          }
          setOptions([]);
        });
      }, 300),
      []
    );

    return (
      <Select<CredentialOptionType>
        leftIcon={FiSearch}
        ref={ref}
        async={true}
        placeholder={"Search Vault Items"}
        value={null} // we don't want to set a value
        defaultOptions={true}
        loadOptions={debounceAsyncGetCredentials}
        key={reloadSelectIndicator}
        onMenuClose={() => setReloadSelectIndicator(reloadSelectIndicator + 1)}
        {...props}
      />
    );
  }
);
